import _extends from "@babel/runtime/helpers/extends";
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
const propTypes = {
  /**
   * Input value text alignment
   */
  align: PropTypes.oneOf(['left', 'right']),
  /**
   * Number of decimal places allowed
   */
  allowDecimals: PropTypes.number,
  /**
   * Character to append to the end of the input
   */
  characterAppend: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * **DEPRECATED** - use `onChange` instead!<br />
   * Callback function for the input value change event<br />
   * `(unformattedValue, formattedValue) => void`
   **/
  handleChange: PropTypes.func,
  /**
   * Enable the input error state
   */
  hasError: PropTypes.bool,
  /**
   * Visually hide the input's label element
   */
  hideLabel: PropTypes.bool,
  /**
   * The input element's id
   **/
  id: PropTypes.string.isRequired,
  /**
   * Content to display in the info popover. Enables the component when provided.
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the info popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Initial formatted input value, must include the characterAppend ("%")
   **/
  initialValue: PropTypes.string,
  /**
   * Additional classes for the input element
   */
  inputClass: PropTypes.string,
  /**
   * Flag to indicate whether this input is a required field
   **/
  isRequired: PropTypes.bool,
  /**
   * Label for the input
   **/
  label: PropTypes.string.isRequired,
  /**
   * Callback function for the input blur event<br />
   * `(event, unformattedValue, formattedValue) => void`
   **/
  onBlur: PropTypes.func,
  /**
   * Callback function for the input change event<br />
   * `(event, unformattedValue, formattedValue) => void`
   **/
  onChange: PropTypes.func,
  /**
   * Callback function for the input focus event<br />
   * `(event) => void`
   **/
  onFocus: PropTypes.func,
  /**
   * Appended to the text label when the input is not required
   */
  optionalInputLabel: PropTypes.string,
  /**
   * Placeholder text for the input
   **/
  placeholder: PropTypes.string,
  /**
   * QA data attribute for automated testing
   **/
  qaId: PropTypes.string,
  /**
   * Enable the selection of the input value on focus
   */
  selectOnFocus: PropTypes.bool,
  /**
   * Append an asterisk to the label when the input is required
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Additional description text to render below the input
   */
  subtext: PropTypes.string
};
const defaultProps = {
  align: 'right',
  allowDecimals: 0,
  characterAppend: '%',
  errorLevel: 'error',
  errorMessage: '',
  hasError: false,
  hideLabel: false,
  infoPopoverContent: null,
  infoPopoverProps: {},
  initialValue: '',
  inputClass: '',
  isRequired: true,
  optionalInputLabel: '(optional)',
  placeholder: '0%',
  qaId: '',
  selectOnFocus: true
};
class PercentInput extends Component {
  constructor(props) {
    super(props);
    this.getUnformattedValue = value => {
      if (this.props.allowDecimals > 0 && value.indexOf('.') > -1) {
        let unformattedValue = value.replace(/[^0-9.]/g, '').split('.');
        // prevents more decimals than allowed
        return unformattedValue[0] + '.' + unformattedValue[1].substring(0, this.props.allowDecimals);
      } else {
        // removes the characterAppend ("%") from the value
        return value.replace(/\D/g, '');
      }
    };
    this.getFormattedValue = unformattedValue => {
      if (undefined === unformattedValue || unformattedValue === '') {
        return '';
      } else if (this.props.allowDecimals > 0 && unformattedValue.indexOf('.') === unformattedValue.length - 1) {
        // if the last character is a decimal point, add a zero to the end
        unformattedValue = unformattedValue + '0';
      } else if (this.props.allowDecimals > 0 && unformattedValue.indexOf('.') === 0) {
        // if the first character is a decimal point, add a zero to the beginning
        unformattedValue = '0' + unformattedValue;
      }
      return unformattedValue + this.props.characterAppend;
    };
    this.onChange = e => {
      const unformattedValue = this.getUnformattedValue(e.target.value);
      const formattedValue = this.getFormattedValue(unformattedValue);
      this.setState({
        value: unformattedValue + this.props.characterAppend
      }, () => {
        const input = this.inputRef?.current;
        if (input && input.selectionStart > unformattedValue.length) {
          // set the cursor to the end of the value, before the characterAppend ("%")
          input.setSelectionRange(unformattedValue.length, unformattedValue.length);
        }
      });
      if (this.props.handleChange) {
        this.props.handleChange(unformattedValue, formattedValue);
      }
      if (this.props.onChange) {
        this.props.onChange(e, unformattedValue, formattedValue);
      }
    };
    this.onBlur = e => {
      const unformattedValue = this.getUnformattedValue(this.state.value);
      const formattedValue = unformattedValue !== '' ? this.getFormattedValue(unformattedValue) : '';
      this.setState({
        value: formattedValue
      });
      if (this.props.onBlur) {
        this.props.onBlur(e, unformattedValue, formattedValue);
      }
    };
    this.onFocus = e => {
      if (this.props.selectOnFocus) {
        const input = this.inputRef?.current;
        if (input) {
          input.setSelectionRange(0, input.value.length - 1);
        }
      }
      if (this.props.onFocus) {
        this.props.onFocus(e);
      }
    };
    this.state = {
      value: this.props.initialValue
    };
    this.inputRef = /*#__PURE__*/React.createRef(null);
  }
  render() {
    // all props are defined here, whether they are used or not,
    // in order to prevent them from being passed to the input through inputPassthrough
    const {
      allowDecimals,
      // eslint-disable-line no-unused-vars
      characterAppend,
      // eslint-disable-line no-unused-vars
      id,
      initialValue,
      // eslint-disable-line no-unused-vars
      label,
      placeholder,
      qaId,
      subtext,
      align,
      hasError,
      errorMessage,
      hideLabel,
      errorLevel,
      isRequired,
      inputClass,
      optionalInputLabel,
      selectOnFocus,
      // eslint-disable-line no-unused-vars
      showRequiredAsterisk,
      infoPopoverContent,
      infoPopoverProps,
      ...inputProps
    } = this.props;
    const wrapperClassNames = cx('asc-percent-input', {
      [`asc-form-input--${errorLevel}`]: hasError
    });
    const inputClassNames = cx('asc-form-input', inputClass, {
      'asc-text--right': align === 'right',
      'asc-text--left': align === 'left'
    });
    const hasInfoPopover = infoPopoverContent !== null;
    const ids = {
      help: [`${id}-help`, hasError && Boolean(errorMessage)],
      subtext: [`${id}-subtext`, Boolean(subtext)],
      popover: [`${id}-popover`, hasInfoPopover]
    };
    const ariaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
    if (inputProps['aria-describedby']) {
      ariaDescribedByIds.push(inputProps['aria-describedby']);
    }
    return /*#__PURE__*/React.createElement("div", {
      className: wrapperClassNames,
      "data-qa": qaId
    }, /*#__PURE__*/React.createElement(FormInputLabel, {
      hideLabel: hideLabel,
      inputId: id,
      isRequired: isRequired,
      label: label,
      optionalInputLabel: optionalInputLabel,
      showRequiredAsterisk: showRequiredAsterisk
    }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
      messagePopoverId: ids.popover[0],
      messagePopoverContent: infoPopoverContent,
      messagePopoverProps: infoPopoverProps
    }), /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
      id: id,
      name: id,
      type: "text",
      className: inputClassNames,
      value: this.state.value,
      onChange: this.onChange,
      onBlur: this.onBlur,
      onFocus: this.onFocus,
      placeholder: placeholder,
      inputMode: "numeric",
      autoComplete: "off",
      "aria-describedby": ariaDescribedByIds.length ? ariaDescribedByIds.join(' ') : null,
      ref: this.inputRef
    })), /*#__PURE__*/React.createElement(FormInputErrors, {
      containerId: ids.help[0],
      errorLevel: errorLevel,
      errorMessage: errorMessage,
      hasError: hasError
    }), subtext && /*#__PURE__*/React.createElement("p", {
      id: ids.subtext[0],
      className: "asc-form-subtext"
    }, subtext));
  }
}
PercentInput.defaultProps = defaultProps;
PercentInput.propTypes = propTypes;
export default PercentInput;